// SPDX-FileCopyrightText: 2025 Slant Tech LLC
// SPDX-License-Identifier: GPL-3.0-or-later

import { useState, useEffect } from 'react';
import api, { ApiError, forceReauthenticate } from '../api';

/**
 * MR Card component for displaying a single merge request
 */
function MRCard({ mr, onSelect }) {
  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'just now';
  };

  return (
    <div
      className="card p-4 cursor-pointer"
      onClick={() => onSelect(mr)}
    >
      <div className="flex items-start gap-3">
        {mr.author?.avatar_url && (
          <img
            src={mr.author.avatar_url}
            alt={mr.author.username}
            className="w-8 h-8 flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-medium text-st-text truncate" title={mr.title}>
            {mr.title}
          </h3>
          <div className="text-sm text-st-muted mt-1 font-body">
            <span className="text-st-accent">{mr.project?.path_with_namespace || `Project ${mr.project_id}`}</span>
            <span className="mx-1">•</span>
            <span>!{mr.mr_iid}</span>
          </div>
          <div className="flex items-center gap-2 mt-2 text-xs text-st-muted font-mono">
            <span>{mr.source_branch}</span>
            <span>→</span>
            <span>{mr.target_branch}</span>
            <span className="mx-1">•</span>
            <span className="font-body">Updated {timeAgo(mr.updated_at)}</span>
          </div>
        </div>
        <div className="flex-shrink-0">
          <span className="inline-flex items-center px-2 py-1 text-xs font-mono font-semibold uppercase tracking-wider bg-st-accent/20 text-st-accent">
            KiCad
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * MR Section component for grouping MRs by category
 */
function MRSection({ title, icon, mrs, onSelectMR, emptyMessage }) {
  if (mrs.length === 0) {
    return (
      <div className="mb-8">
        <h2 className="text-lg font-display font-semibold text-st-text mb-4 flex items-center gap-2">
          <span>{icon}</span>
          <span>{title}</span>
          <span className="text-sm font-normal text-st-muted">(0)</span>
        </h2>
        <div className="text-st-muted text-sm py-4 text-center bg-st-secondary border-2 border-dashed border-st-border-subtle font-body">
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <h2 className="text-lg font-display font-semibold text-st-text mb-4 flex items-center gap-2">
        <span>{icon}</span>
        <span>{title}</span>
        <span className="text-sm font-normal text-st-muted">({mrs.length})</span>
      </h2>
      <div className="grid gap-3">
        {mrs.map((mr) => (
          <MRCard
            key={`${mr.project_id}-${mr.mr_iid}`}
            mr={mr}
            onSelect={onSelectMR}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Dashboard component showing MRs with KiCad files
 */
export default function Dashboard({ currentUser, onLogout, theme, onToggleTheme }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mrData, setMrData] = useState({
    assigned: [],
    review_requested: [],
    participating: []
  });

  useEffect(() => {
    async function loadMRs() {
      try {
        setLoading(true);
        setError(null);
        const data = await api.getDashboardMergeRequests();
        setMrData(data);
      } catch (err) {
        console.error('Failed to load MRs:', err);

        // Check for GitLab token errors that require re-authentication
        if (err instanceof ApiError) {
          if (err.data?.detail === 'gitlab_token_invalid' || err.data?.action === 'reauthenticate') {
            console.warn('GitLab token invalid, forcing re-authentication');
            if (!err.data?.redirecting) {
              forceReauthenticate();
            }
            return;
          }
        }

        setError(err.message || 'Failed to load merge requests');
      } finally {
        setLoading(false);
      }
    }

    loadMRs();
  }, []);

  const handleSelectMR = (mr) => {
    // Navigate to the review page
    window.location.href = `/review/${mr.project_id}/${mr.mr_iid}`;
  };

  const totalMRs = mrData.assigned.length + mrData.review_requested.length + mrData.participating.length;

  return (
    <div className="min-h-screen bg-st-primary">
      {/* Header */}
      <header className="bg-st-secondary border-b-2 border-st-border px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <i className="bx bx-vector text-2xl text-st-accent"></i>
            <div>
              <h1 className="text-xl font-display font-semibold text-st-text">KiCad Review</h1>
              <p className="text-sm text-st-muted font-body">Hardware Design Review Tool</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Theme toggle */}
            <button
              onClick={onToggleTheme}
              className="p-1.5 text-st-muted hover:text-st-accent transition-colors"
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            {currentUser && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  {currentUser.avatar_url && (
                    <img
                      src={currentUser.avatar_url}
                      alt={currentUser.username}
                      className="w-8 h-8"
                    />
                  )}
                  <span className="text-sm text-st-text font-body">{currentUser.name || currentUser.username}</span>
                </div>
                <button
                  onClick={onLogout}
                  className="text-sm text-st-muted hover:text-st-text transition-colors font-display font-semibold uppercase tracking-wider"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="spinner w-8 h-8 mx-auto mb-4" />
              <p className="text-st-muted font-body">Loading merge requests...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-st-error/10 border-2 border-st-error p-4 text-st-error">
            <p className="font-display font-medium">Error loading merge requests</p>
            <p className="text-sm mt-1 font-body">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 text-sm underline hover:no-underline font-display font-semibold"
            >
              Try again
            </button>
          </div>
        ) : totalMRs === 0 ? (
          <div className="text-center py-20">
            <i className="bx bx-list-ul text-6xl mb-4 text-st-muted"></i>
            <h2 className="text-xl font-display font-semibold text-st-text mb-2">No KiCad merge requests found</h2>
            <p className="text-st-muted max-w-md mx-auto font-body">
              You don't have any open merge requests with KiCad files.
              Create a merge request that includes .kicad_sch or .kicad_pcb files to see it here.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <p className="text-st-muted font-body">
                Showing {totalMRs} open merge request{totalMRs !== 1 ? 's' : ''} with KiCad files
              </p>
            </div>

            <MRSection
              title="Assigned to You"
              icon={<i className="bx bx-user"></i>}
              mrs={mrData.assigned}
              onSelectMR={handleSelectMR}
              emptyMessage="No merge requests assigned to you"
            />

            <MRSection
              title="Review Requested"
              icon={<i className="bx bx-show"></i>}
              mrs={mrData.review_requested}
              onSelectMR={handleSelectMR}
              emptyMessage="No merge requests awaiting your review"
            />

            <MRSection
              title="Your Merge Requests"
              icon={<i className="bx bx-edit"></i>}
              mrs={mrData.participating}
              onSelectMR={handleSelectMR}
              emptyMessage="You haven't created any merge requests with KiCad files"
            />
          </>
        )}
      </main>
    </div>
  );
}
